import { Controller } from "@hotwired/stimulus"

const DRAG_THRESHOLD = 4

// Dragging from the palette and reordering inside the canvas share one
// mechanism. Valid drop targets come from the nesting rules in the schema, so
// a new node type needs no change here.
export default class extends Controller {
  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.root.addEventListener("builder:canvas-ready", (event) => this.bindCanvas(event))
    this.element.addEventListener("pointerdown", (event) => this.onPalettePointerDown(event))
    // A press over the canvas may be delivered to this document rather than
    // the iframe's, depending on how the pointer entered it. Handling both
    // sources keeps dragging reliable; begin() ignores the second one.
    this.onRootPointerDown = (event) => this.onParentPointerDown(event)
    window.addEventListener("pointerdown", this.onRootPointerDown, true)
    this.onMove = (event) => this.onPointerMove(event)
    this.onUp = (event) => this.onPointerUp(event)
  }

  disconnect() {
    this.stopTracking()
    window.removeEventListener("pointerdown", this.onRootPointerDown, true)
    this.line?.remove()
  }

  get builder() { return this.root.builder }
  get store() { return this.builder.store }
  get schema() { return this.builder.schema }
  get frame() { return this.root.querySelector("iframe") }
  get canvasDoc() { return this.frame?.contentDocument }

  // --- starting a drag ----------------------------------------------------

  onPalettePointerDown(event) {
    const button = event.target.closest("[data-type]")
    if (!button) return

    this.begin(event, { kind: "create", type: button.dataset.type })
  }

  bindCanvas() {
    this.canvasDoc?.addEventListener("pointerdown", (event) => this.onCanvasPointerDown(event))
  }

  onCanvasPointerDown(event) {
    if (event.target.isContentEditable) return

    const element = event.target.closest("[data-n]")
    if (!element || element.dataset.t === "page") return

    const rect = this.frame.getBoundingClientRect()
    this.beginMove(element, { clientX: event.clientX + rect.left, clientY: event.clientY + rect.top })
  }

  // Same gesture, seen from the parent document: hit-test into the canvas
  // ourselves instead of relying on the iframe delivering the event.
  onParentPointerDown(event) {
    const frame = this.frame
    if (!frame || event.target !== frame) return

    const rect = frame.getBoundingClientRect()
    const doc = this.canvasDoc
    if (!doc) return

    const hit = doc.elementFromPoint(event.clientX - rect.left, event.clientY - rect.top)
    const element = hit?.closest("[data-n]")
    if (!element || element.dataset.t === "page") return
    if (element.isContentEditable && doc.activeElement === element) return

    this.beginMove(element, event)
  }

  beginMove(element, point) {
    this.begin(point, { kind: "move", type: element.dataset.t, id: element.dataset.n })
  }

  begin(event, payload) {
    // Both pointerdown sources can fire for one press; the first wins.
    if (this.drag) return

    this.drag = { ...payload, startX: event.clientX, startY: event.clientY, active: false }
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp)
    // The canvas lives in another document, so it needs its own listeners.
    this.canvasDoc?.addEventListener("pointermove", this.onMove)
    this.canvasDoc?.addEventListener("pointerup", this.onUp)
  }

  stopTracking() {
    window.removeEventListener("pointermove", this.onMove)
    window.removeEventListener("pointerup", this.onUp)
    this.canvasDoc?.removeEventListener("pointermove", this.onMove)
    this.canvasDoc?.removeEventListener("pointerup", this.onUp)
  }

  // --- tracking -----------------------------------------------------------

  // Canvas pointer events report coordinates inside the iframe; palette ones
  // report viewport coordinates. Everything below works in viewport space.
  toViewport(event) {
    const rect = this.frame.getBoundingClientRect()
    const insideCanvas = event.view === this.frame.contentWindow
    return insideCanvas
      ? { x: event.clientX + rect.left, y: event.clientY + rect.top }
      : { x: event.clientX, y: event.clientY }
  }

  onPointerMove(event) {
    if (!this.drag) return

    const point = this.toViewport(event)
    if (!this.drag.active) {
      const moved = Math.hypot(point.x - this.drag.startX, point.y - this.drag.startY)
      if (moved < DRAG_THRESHOLD) return
      this.drag.active = true
      this.markDragging(true)
    }

    event.preventDefault?.()
    this.drop = this.findDropTarget(point)
    this.drawLine(this.drop)
  }

  async onPointerUp() {
    const drag = this.drag
    const drop = this.drop
    this.stopTracking()
    this.markDragging(false)
    this.clearLine()
    this.drag = null
    this.drop = null

    if (!drag?.active || !drop) return

    if (drag.kind === "create") {
      await this.builder.createNode(drag.type, drop.parentId, drop.index)
    } else if (drag.id !== drop.parentId) {
      this.store.move(drag.id, drop.parentId, drop.index)
    }
  }

  markDragging(on) {
    if (!this.drag) return
    if (this.drag.kind !== "move") return
    const element = this.canvasDoc?.querySelector(`[data-n="${CSS.escape(this.drag.id)}"]`)
    element?.classList.toggle("builder-dragging", on)
  }

  // --- hit testing --------------------------------------------------------

  accepts(parentType, childType) {
    return (this.schema.children[parentType] || []).includes(childType)
  }

  // The deepest element under the pointer that can hold the dragged type,
  // plus the insertion index taken from its children's midpoints.
  findDropTarget(point) {
    const doc = this.canvasDoc
    const rect = this.frame.getBoundingClientRect()
    if (!doc) return null

    const local = { x: point.x - rect.left, y: point.y - rect.top }
    if (local.x < 0 || local.y < 0 || local.x > rect.width || local.y > rect.height) return null

    let element = doc.elementFromPoint(local.x, local.y)?.closest("[data-n]")

    while (element) {
      if (this.accepts(element.dataset.t, this.drag.type) && !this.insideDragged(element)) {
        return { parentId: element.dataset.n, index: this.insertionIndex(element, local), element }
      }
      element = element.parentElement?.closest("[data-n]")
    }

    return this.fallbackTarget()
  }

  // A drop in the empty space below the content should append rather than do
  // nothing, which is what the user means by dropping at the bottom.
  fallbackTarget() {
    const root = this.canvasDoc?.querySelector('[data-t="page"]')
    if (!root) return null

    if (this.accepts("page", this.drag.type)) {
      return { parentId: root.dataset.n, index: root.children.length, element: root }
    }

    const candidates = [...root.children].filter(
      (child) => child.dataset.n && this.accepts(child.dataset.t, this.drag.type) && !this.insideDragged(child)
    )
    const last = candidates[candidates.length - 1]
    if (!last) return null

    return { parentId: last.dataset.n, index: last.children.length, element: last }
  }

  // Never drop a node into its own subtree.
  insideDragged(element) {
    if (this.drag.kind !== "move") return false
    const dragged = this.canvasDoc.querySelector(`[data-n="${CSS.escape(this.drag.id)}"]`)
    return dragged ? dragged.contains(element) : false
  }

  insertionIndex(parent, local) {
    const children = [...parent.children].filter((child) => child.dataset.n)
    if (!children.length) return 0

    const horizontal = getComputedStyle(parent).flexDirection === "row"
    for (let index = 0; index < children.length; index += 1) {
      const box = children[index].getBoundingClientRect()
      const middle = horizontal ? box.left + box.width / 2 : box.top + box.height / 2
      const position = horizontal ? local.x : local.y
      if (position < middle) return index
    }
    return children.length
  }

  // --- drop indicator -----------------------------------------------------

  drawLine(drop) {
    if (!drop) return this.clearLine()

    const doc = this.canvasDoc
    this.line = this.line?.isConnected ? this.line : doc.createElement("div")
    this.line.className = "builder-drop-line"

    const children = [...drop.element.children].filter((child) => child.dataset.n)
    const horizontal = getComputedStyle(drop.element).flexDirection === "row"
    const parentBox = drop.element.getBoundingClientRect()
    const scroll = { x: doc.documentElement.scrollLeft, y: doc.documentElement.scrollTop }

    let box
    let after = false
    if (!children.length) {
      box = parentBox
    } else if (drop.index >= children.length) {
      box = children[children.length - 1].getBoundingClientRect()
      after = true
    } else {
      box = children[drop.index].getBoundingClientRect()
    }

    Object.assign(this.line.style, horizontal
      ? {
          left: `${(after ? box.right : box.left) + scroll.x - 1}px`,
          top: `${box.top + scroll.y}px`,
          width: "3px",
          height: `${box.height}px`
        }
      : {
          left: `${box.left + scroll.x}px`,
          top: `${(after ? box.bottom : box.top) + scroll.y - 1}px`,
          width: `${box.width}px`,
          height: "3px"
        })

    doc.body.append(this.line)
  }

  clearLine() {
    this.line?.remove()
  }
}
